import { inject, Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { BaseApiService } from '@packages/ng-shared-services';
import { ACCOUNT_MANAGER_ENDPOINTS } from '@packages/shared-types';
import { Observable, throwError } from 'rxjs';
import { ProgramService } from '../../../../services/program/program.service';
import { Params } from '@angular/router';

export interface AccountManager {
  id: number;
  username: string;
  email: string;
  role_name: string;
  role_id: number;
  is_active: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AccountManagerService {
  private api = inject(BaseApiService);
  private programService = inject(ProgramService);

  /**
   * Get the X-Program-Id header value
   * - Returns program UID if viewing an existing program
   * - Returns 'master_admin' if creating new program or no program selected
   */
  private getProgramIdHeader(): string {
    const programUid = this.programService.programUid();
    return programUid || 'master_admin';
  }

  /**
   * Get all account managers
   * Endpoint: admin/get-manager
   * @param userRoleId - Optional role ID (number) or comma-separated role IDs (string) for filtering (e.g., 3 or "1,2,3")
   */
  getAccountManagers(userRoleId?: string | number): Observable<AccountManager[]> {
    const params = userRoleId
      ? { user_role_id: typeof userRoleId === 'number' ? userRoleId.toString() : userRoleId }
      : undefined;
    return this.api
      .getWithHeaders(
        ACCOUNT_MANAGER_ENDPOINTS.list,
        {
          'X-Program-Id': this.getProgramIdHeader(),
        },
        params
      )
      .pipe(
        map((response: any) => {
          const data = response.body || response;
          const users = data.data?.users || [];
          // Map API response to AccountManager interface
          return users.map((user: any) => ({
            id: user.id,
            username: user.username,
            email: user.email,
            role_name: user.role_name,
            role_id: user.user_role_id,
            is_active: user.status === 1, // Convert status (1/0) to boolean
          }));
        }),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Create account manager
   * Endpoint: admin/create-manager
   * Payload: { email, role_id, username }
   */
  createAccountManager(data: {
    email: string;
    role_id: number;
    username: string;
  }): Observable<any> {
    return this.api
      .postWithHeaders(ACCOUNT_MANAGER_ENDPOINTS.create, data, {
        'X-Program-Id': this.getProgramIdHeader(),
      })
      .pipe(
        map((response: any) => response.body || response),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Update account manager
   * Endpoint: admin/update-manager
   * Payload: { current_email, email, role_id, username }
   */
  updateAccountManager(data: {
    current_email: string;
    email: string;
    role_id: number;
    username: string;
    manager_id: number;
  }): Observable<any> {
    return this.api
      .postWithHeaders(ACCOUNT_MANAGER_ENDPOINTS.update, data, {
        'X-Program-Id': this.getProgramIdHeader(),
      })
      .pipe(
        map((response: any) => response.body || response),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Check delete eligibility and get confirmation details
   * Endpoint: admin/delete-confirm
   * Payload: { email }
   * Note: Called before deactivating to get confirmation details (programs mapped, etc.)
   */
  checkDeleteEligibility(email: string): Observable<any> {
    return this.api
      .postWithHeaders(
        ACCOUNT_MANAGER_ENDPOINTS.deleteConfirm,
        { email },
        {
          'X-Program-Id': this.getProgramIdHeader(),
        }
      )
      .pipe(
        map((response: any) => response.body || response),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Update account manager status (activate/deactivate)
   * Endpoint: admin/delete-manager
   * Payload: { email, status }
   * Note:
   * - For activation (status: 1): Called directly when toggling inactive to active
   * - For deactivation (status: 0): Called after user confirms in modal
   */
  updateManagerStatus(email: string, status: number): Observable<any> {
    return this.api
      .postWithHeaders(
        ACCOUNT_MANAGER_ENDPOINTS.delete,
        { email, status },
        {
          'X-Program-Id': this.getProgramIdHeader(),
        }
      )
      .pipe(
        map((response: any) => response.body || response),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }
}
